import React, { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import JjfShell, { Heart } from "./JjfShell";

/* ---- testimonial avatars ---- */
import m from "../../images/auth/minister.webp";
import N from "../../images/auth/DeviShukla.webp";
import P from "../../images/auth/AshokMaheshwari.webp";
import Q from "../../images/auth/SaurabhSonkar.webp";
import R from "../../images/auth/SanjaySrivastava.webp";

import HeroHands from "../../images/illus/hero-image-hands.webp";

import useRandomPopup from "../../hooks/useRandomPopup";
import PopupModal from "../popupModal/PopUpModel";

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";

/* ---- section imagery (real foundation photos from /public/images/home-v2) ---- */
const HOME_IMG = process.env.PUBLIC_URL + "/images/home-v2/";
const HeroPhoto = HOME_IMG + "hero.jpg";
const HomeBanner2 = HOME_IMG + "about.jpg";
const JoinSectionImg = HOME_IMG + "join.jpg";
const HomeVolunteerHero = HOME_IMG + "volunteer.jpg";

/* Life Saver Army logo lives in /public (filename contains spaces) */
const LsaLogo = process.env.PUBLIC_URL + "/images/lsa-logo.png";

/* ============================================================
   STATIC DATA
   ============================================================ */
const LSA_INTRO =
  "Our flagship initiative; a nationwide blood and platelet donor network connecting voluntary donors with patients in urgent need, powered by QR-code registration, real-time outreach, and deep community partnerships across nine Indian states.";

const LSA_FEATURES = [
  {
    title: "Donor Registration Network",
    description:
      "A QR-code-based system that allows anyone to register as a blood or platelet donor in minutes; deployed at hospitals, colleges, railway stations, and public spaces across nine states.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8" /><rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8" /><rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8" /><path d="M14 14h3v3M21 14v.01M17 21h4M21 17v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
    ),
  },
  {
    title: "Emergency Blood Matching",
    description:
      "A rapid-response process connecting patients and families in crisis with verified, nearby donors; available around the clock for all blood groups including rare types.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none"><path d="M3 12h4l2-5 4 10 2-5h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    ),
  },
  {
    title: "Blood Donation Camps",
    description:
      "Organised donation drives at universities, hospitals, and community centres that collect blood units and register new donors; building the network one camp at a time.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none"><path d="M12 3 3 20h18L12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M12 3v17" stroke="currentColor" strokeWidth="1.8" /></svg>
    ),
  },
  {
    title: "Hospital and NGO Partnerships",
    description:
      "Formal agreements with blood banks, hospitals, and NGOs across Uttar Pradesh, Bihar, Maharashtra, Goa, Rajasthan, Delhi, Uttarakhand, Bengaluru, and Hyderabad that enable fast, trusted emergency blood access for patients.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none"><path d="M4 21V7l8-4 8 4v14M4 21h16M10 12h4M12 10v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    ),
  },
  {
    title: "Donor Awareness and Education",
    description:
      "Community-led awareness drives, digital campaigns, and campus outreach programmes that educate the public on blood donation eligibility, safety, and impact; growing a culture of voluntary donation.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none"><path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M16 8a4 4 0 0 1 0 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
    ),
  },
  {
    title: "Platelet Donation Programme",
    description:
      "A dedicated stream within Life Saver Army for platelet donors and recipients, supporting cancer, thalassaemia, and dengue patients who require regular platelet transfusions.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none"><path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
    ),
  },
  {
    title: "Volunteer Field Network",
    description:
      "A nationwide team of trained Life Saver Army field coordinators and community volunteers who support donor drives, manage registrations, and ensure emergency requests are met with speed and care.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none"><path d="M17 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm11 9v-2a4 4 0 0 0-3-3.87M16 4.13A4 4 0 0 1 16 11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    ),
  },
  {
    title: "Corporate CSR Partnerships",
    description:
      "Collaborating with companies through CSR initiatives to support blood donation camps, donor awareness, community outreach, and emergency blood access; creating sustainable impact through corporate participation.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none"><path d="M3 21h18M5 21V9l7-4 7 4v12M9 21v-4h6v4M9 12h.01M12 12h.01M15 12h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    ),
  },
];

const TESTIMONIALS = [
  {
    img: m,
    quote:
      "It was an honour to join Jainam Jivika Foundation's blood donation drive and witness the volunteers' spirit of selfless service. Every donation is a gift of life, and such initiatives inspire compassion and community service.",
    name: "Shri Dayashankar Mishra",
    role: "Honourable Minister, Uttar Pradesh",
  },
  {
    img: N,
    quote:
      "When my father needed blood urgently, we had no idea where to turn. A Life Saver Army volunteer reached us within the hour. The platform is a lifeline, and what Jainam Jivika Foundation has built gives families like ours genuine hope.",
    name: "Family of a Blood Recipient",
    role: "Varanasi",
  },
  {
    img: P,
    quote:
      "Registering as a Life Saver Army donor was one of the simplest things I have ever done; and the most meaningful. The team is responsive, the process is fast, and knowing my name is on that list makes me feel like I am genuinely part of something that saves lives.",
    name: "Registered Blood Donor",
    role: "Lucknow",
  },
  {
    img: Q,
    quote:
      "Coordinating our outreach initiatives has been incredibly rewarding. Seeing communities come together and witnessing the tangible impact of our work reminds me why I do this every day. Every volunteer, every participant, contributes to a greater change.",
    name: "Saurabh Sonkar",
    role: "Outreach & Partnerships Lead",
  },
  {
    img: R,
    quote:
      "Our hospital has seen how quickly Life Saver Army can mobilise donors in an emergency. The partnership with Jainam Jivika Foundation has genuinely improved blood availability for our patients, and we are proud to have QR code stickers at every entrance.",
    name: "Hospital Administrator",
    role: "Ram Krishna Mission Hospital, Varanasi",
  },
];

const MARQUEE = [
  "Community Impact",
  "Healthcare & Wellbeing",
  "Blood Donation",
  "Volunteer Engagement",
  "Emergency Support",
  "Social Change",
];

/* ============================================================
   COMPONENT
   ============================================================ */
const MainPage = ({ path }) => {
  const { selectedPopup, popupShown, handleClosePopup } = useRandomPopup();

  const [metadata, setMetadata] = useState({ title: "JJFIndia | " });
  const [schemadata, setSchemadata] = useState({});
  const [activeTab, setActiveTab] = useState("mission");
  const [tIdx, setTIdx] = useState(0);

  const scrollTop = () => window.scrollTo(10, 0);

  useEffect(() => {
    let tempMetadata = metadataJSON[path];
    let tempSchemadata = schemadataJSON[path];
    if (tempMetadata) {
      setMetadata(tempMetadata);
    } else {
      setMetadata({
        title: "JJFIndia | Home",
        description: "JJFIndia home page",
        keywords: "",
        robots: "index, follow",
        canonical: "https://jjfindia.com/",
        openGraph: { title: "JJFIndia | Home", description: "JJFIndia home page", url: "https://jjfindia.com/" },
        twitter: { card: "summary_large_image", title: "JJFIndia | Home", description: "JJFIndia home page" },
      });
    }
    if (tempSchemadata) setSchemadata(tempSchemadata);
  }, [path]);

  /* Testimonial slider: a scroll-snap track (3 cards on desktop, 2 on tablet,
     1 on phones, set in CSS). tIdx is the first visible card; tPages is how many
     distinct positions the track has at the current width. */
  const tTrack = useRef(null);
  const [tPages, setTPages] = useState(TESTIMONIALS.length);
  const tStep = () => {
    const el = tTrack.current;
    if (!el || !el.children.length) return 1;
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    return el.children[0].offsetWidth + gap;
  };
  const tGo = (i) => {
    const el = tTrack.current;
    if (!el) return;
    const n = ((i % tPages) + tPages) % tPages;
    el.scrollTo({ left: n * tStep(), behavior: "smooth" });
  };
  useEffect(() => {
    const el = tTrack.current;
    if (!el) return;
    const measure = () => setTPages(Math.round((el.scrollWidth - el.clientWidth) / tStep()) + 1);
    const onScroll = () => setTIdx(Math.round(el.scrollLeft / tStep()));
    measure();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
    };
  }, []);
  // Advance every 6.5s; restarts on every move so a manual pick gets its full time.
  const [tPaused, setTPaused] = useState(false);
  useEffect(() => {
    if (tPaused || tPages < 2) return undefined;
    const timer = setTimeout(() => tGo(tIdx + 1), 6500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tIdx, tPages, tPaused]);

  return (
    <Fragment>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="robots" content={metadata.robots} />
        <link rel="canonical" href={metadata.canonical} />
        <meta property="og:title" content={metadata.openGraph?.title} />
        <meta property="og:description" content={metadata.openGraph?.description} />
        <meta property="og:url" content={metadata.canonical} />
        <meta name="twitter:title" content={metadata.twitter?.title} />
        <meta name="twitter:description" content={metadata.twitter?.description} />
        <script type="application/ld+json">{JSON.stringify(schemadata, null, 2)}</script>
      </Helmet>

      <JjfShell>
        {/* ===================== HERO ===================== */}
        <section className="hero hero--photo" id="home">
          <div className="hero-mesh" aria-hidden="true"></div>
          <div className="wrap hero-grid">
            <div className="hero-copy">
              <span className="tag reveal"><Heart /> Jainam Jivika Foundation</span>
              <h1 className="reveal d1">
                <span className="word"><i>Every</i></span>{" "}
                <span className="word"><i>Second</i></span>{" "}
                <span className="word"><i>Counts.</i></span>{" "}
                <span className="word"><i>Every</i></span>{" "}
                <span className="word"><i className="accent">Drop</i></span>{" "}
                <span className="word"><i>Saves</i></span>{" "}
                <span className="word"><i>a</i></span>{" "}
                <span className="word"><i className="accent">Life.</i></span>
              </h1>
              <p className="lead reveal d2">
                Jainam Jivika Foundation is a social organisation committed to building stronger, healthier, and more compassionate communities across India. At the heart of our work is Life Saver Army; our flagship initiative connecting voluntary blood donors with patients in urgent need, ensuring life-saving blood reaches people exactly when it matters most.
              </p>
              <div className="hero-actions reveal d3">
                <a href="https://lifesaverarmy.com/volunteers/registration" target="_blank" rel="noopener noreferrer" className="btn btn--green">Join the Mission →</a>
                <Link to="/initiatives/life-saver-army" onClick={scrollTop} className="btn btn--ghost">Learn More</Link>
              </div>
              <div className="cred-strip reveal d4">
                <div className="cred"><span className="n">8.8K<b>+</b></span><span className="l">Total registrations</span></div>
                <div className="cred"><span className="n">106<b>+</b></span><span className="l">Blood camps</span></div>
                <div className="cred"><span className="n">9</span><span className="l">States reached</span></div>
                <div className="cred"><span className="n">140K<b>+</b></span><span className="l">Volunteers</span></div>
              </div>
              {/* Navigation anchor from the content doc: jumps to the footer */}
              <button
                type="button"
                className="scroll-cue reveal d4"
                style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
                onClick={() => document.getElementById("footer")?.scrollIntoView({ behavior: "smooth" })}
              >
                <span className="mouse" aria-hidden="true"></span>
                Go to bottom
              </button>
            </div>

          </div>
          <div className="hero-bg">
            <img src={HeroPhoto} alt="Donors at a Life Saver Army blood donation camp" loading="eager" />
          </div>
        </section>

        {/* ===================== ABOUT ===================== */}
        <section className="section about" id="about">
          <div className="wrap split">
            <div className="about-media about-media--leaf reveal">
              <span className="leaf-frame" aria-hidden="true"></span>
              <div className="ph">
                <img src={HomeBanner2} alt="Jainam Jivika Foundation volunteers serving the community" loading="lazy" />
              </div>
              <div className="stat-card reveal d2"><div className="n">Since 2017</div><div className="l">Serving communities</div></div>
            </div>
            <div className="about-copy reveal d1">
              <span className="eyebrow">About Jainam Jivika Foundation</span>
              <h2>Helping communities, changing lives</h2>
              <p className="lead">
                Jainam Jivika Foundation is a social organisation dedicated to improving lives and driving meaningful change across communities. Through focused initiatives, we address the issues that matter most; starting with the most urgent: access to life-saving blood during medical emergencies.
              </p>
              <p className="lead">
                Our flagship initiative, Life Saver Army, is a nationwide blood donor network that connects voluntary donors with patients in crisis. By combining digital technology with deep community engagement, we are building the infrastructure that ensures no patient loses their life simply because blood was not available.
              </p>
              <p className="lead">
                We believe lasting social change requires more than goodwill; it demands strategy, community trust, and unwavering commitment. That is what Jainam Jivika Foundation stands for.
              </p>
              <div className="about-cta">
                <Link to="/join-us/donate" onClick={scrollTop} className="btn btn--green" data-burst="1">
                  Be a Part of the Change <Heart />
                </Link>
              </div>
            </div>
          </div>

          <div className="wrap">
            <span className="eyebrow reveal" style={{ marginTop: "10px" }}>Why Choose Jainam Jivika Foundation</span>
            <div className="pillars">
              <article className="pillar reveal">
                <div className="pic"><svg viewBox="0 0 24 24" fill="none"><path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l2.5 2.5M16.5 16.5 19 19M19 5l-2.5 2.5M7.5 16.5 5 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" /></svg></div>
                <h3>Big Impact</h3>
                <p>We take on urgent, real-world challenges with practical, community-driven solutions; starting with India's emergency blood shortage.</p>
              </article>
              <article className="pillar reveal d1">
                <div className="pic"><svg viewBox="0 0 24 24" fill="none"><path d="M17 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm11 9v-2a4 4 0 0 0-3-3.87M16 4.13A4 4 0 0 1 16 11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                <h3>Community Focused</h3>
                <p>We build strong networks of donors, volunteers, and supporters who show up for each other when it counts.</p>
              </article>
              <article className="pillar reveal d2">
                <div className="pic"><svg viewBox="0 0 24 24" fill="none"><path d="M12 3v3M5 6l2 2M19 6l-2 2M4 13h2M18 13h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><path d="M9 18h6M10 21h4M12 6a6 6 0 0 0-3 11h6a6 6 0 0 0-3-11Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg></div>
                <h3>Future Ready</h3>
                <p>We combine digital innovation with grassroots outreach to create impact that is scalable, sustainable, and built to last.</p>
              </article>
            </div>
          </div>
        </section>

        {/* ===================== IMPACT BAND ===================== */}
        <section className="section impact" id="impact">
          <div className="bg-layer" aria-hidden="true">
            <div className="ov"></div>
          </div>
          <div className="wrap">
            <div className="impact-pair">
              <div>
                <div className="big reveal d1"><span className="count" data-target="8.8" data-decimals="1" data-suffix="K">0</span><span className="big-plus">+</span></div>
                <p className="sub reveal d2">Total registrations on Life Saver Army nationwide</p>
              </div>
              <div>
                <div className="big reveal d1"><span className="count" data-target="106">0</span><span className="big-plus">+</span></div>
                <p className="sub reveal d2">Successful blood camps organised</p>
              </div>
            </div>

            <a href="https://lifesaverarmy.com/volunteers/registration" target="_blank" rel="noopener noreferrer" className="btn btn--green reveal d2" style={{ marginTop: "34px" }}>Join the Mission →</a>
          </div>
          <img className="imp-hands" src={HeroHands} alt="" aria-hidden="true" loading="lazy" />
          <div className="viral-marquee" aria-hidden="true">
            <div className="track">
              {[...MARQUEE, ...MARQUEE].map((t, i) => (<span key={i}>{t}</span>))}
            </div>
          </div>
        </section>

        {/* ===================== MISSION / VISION / VALUES ===================== */}
        <section className="section section--mist">
          <div className="wrap">
            <span className="eyebrow reveal">What drives us</span>
            <h2 className="reveal d1" style={{ fontSize: "clamp(2rem,4.2vw,3.2rem)", maxWidth: "none", lineHeight: 1.32 }}>Our purpose, vision and values</h2>
            <div className="mvv">
              <div className="mvv-tabs reveal">
                <button className={"mvv-tab" + (activeTab === "mission" ? " active" : "")} onClick={() => setActiveTab("mission")}><span className="dot"></span><span>Our Mission</span></button>
                <button className={"mvv-tab" + (activeTab === "vision" ? " active" : "")} onClick={() => setActiveTab("vision")}><span className="dot"></span><span>Our Vision</span></button>
                <button className={"mvv-tab" + (activeTab === "values" ? " active" : "")} onClick={() => setActiveTab("values")}><span className="dot"></span><span>Our Values</span></button>
              </div>
              <div className="mvv-panel-box reveal d1">
                <div className={"mvv-panel" + (activeTab === "mission" ? " show" : "")}>
                  <div className="pico"><svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="1.8" /><circle cx="12" cy="12" r="5" stroke="#fff" strokeWidth="1.8" /><circle cx="12" cy="12" r="1.6" fill="#fff" /></svg></div>
                  <h3>Our Mission</h3>
                  <p>At our core, we are a social foundation dedicated to empowering individuals, strengthening communities, and driving lasting impact. Through Life Saver Army, our flagship initiative, we are addressing one of India's most urgent and preventable crises, the shortage of emergency blood, by building a nationwide network of voluntary donors, trained volunteers, and committed healthcare partners.</p>
                </div>
                <div className={"mvv-panel" + (activeTab === "vision" ? " show" : "")}>
                  <div className="pico"><svg viewBox="0 0 24 24" fill="none"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" /><circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="1.8" /></svg></div>
                  <h3>Our Vision</h3>
                  <p>A future where no patient loses their life for want of blood. A nation where voluntary donation is a social norm, and every community has the network to support its own in times of crisis.</p>
                </div>
                <div className={"mvv-panel" + (activeTab === "values" ? " show" : "")}>
                  <div className="pico"><Heart /></div>
                  <h3>Our Values</h3>
                  <ul className="mvv-values">
                    <li><h6>Urgency</h6><p>We act swiftly because lives cannot wait.</p></li>
                    <li><h6>Compassion</h6><p>Every donor registration and every donation camp is an act of care for human life.</p></li>
                    <li><h6>Integrity</h6><p>We operate with full transparency and hold ourselves accountable to our community.</p></li>
                    <li><h6>Community</h6><p>We believe lasting change is built person by person, not institution by institution.</p></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===================== INITIATIVE (Life Saver Army) ===================== */}
        <section className="section" id="initiatives">
          <div className="wrap">
            <div className="reveal" style={{ maxWidth: "760px" }}>
              <span className="eyebrow">Our Initiative</span>
              <h2 style={{ lineHeight: 1.34 }}>Creating impact through our transformative initiatives</h2>
            </div>

            <div className="lsa-flagship reveal d1">
              <div className="lsa-logo"><img src={LsaLogo} alt="Life Saver Army logo" loading="lazy" /></div>
              <div className="lsa-body">
                <span className="lsa-tag">Our Flagship Initiative</span>
                <h3>Life Saver Army</h3>
                <p>{LSA_INTRO}</p>
                <a href="https://lifesaverarmy.com/" target="_blank" rel="noopener noreferrer" className="btn btn--green">Explore Life Saver Army →</a>
              </div>
            </div>

            <span className="eyebrow reveal" style={{ marginTop: "56px" }}>Inside Life Saver Army</span>
            <div className="init-grid">
              {LSA_FEATURES.map((f, i) => (
                <article className={"init reveal" + (i % 4 ? " d" + (i % 4) : "")} key={f.title}>
                  <span className="glow"></span>
                  <div className="badge">{f.icon}</div>
                  <h3>{f.title}</h3>
                  <p>{f.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== JOIN HANDS ===================== */}
        <section className="section section--mist join">
          <div className="wrap split">
            <div className="join-media reveal">
              <div className="ph">
                <img src={JoinSectionImg} alt="Join hands to uplift communities" loading="lazy" />
              </div>
            </div>
            <div className="join-copy reveal d1">
              <span className="eyebrow">Be a part of the change!</span>
              <h2>Join hands to uplift communities together</h2>
              <p className="lead">
                Become a volunteer and put your time and skills to work for communities that need it most. If volunteering is not possible right now, support our initiatives or partner with us to help drive lasting change and create brighter futures for those in need.
              </p>
              <a href="https://lifesaverarmy.com/volunteers" target="_blank" rel="noopener noreferrer" className="btn btn--green">
                Become a Volunteer <Heart />
              </a>
            </div>
          </div>
        </section>

        {/* ===================== WAYS TO CONTRIBUTE ===================== */}
        <section className="section" id="contribute">
          <div className="wrap" style={{ textAlign: "center" }}>
            <span className="eyebrow reveal" style={{ justifyContent: "center" }}>Need your support</span>
            <h2 className="reveal d1" style={{ fontSize: "clamp(2rem,4.2vw,3.2rem)" }}>Ways you can contribute</h2>
          </div>
          <div className="wrap">
            <div className="ways">
              <article className="way reveal">
                <div className="wic"><svg viewBox="0 0 24 24" fill="none"><path d="M17 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm11 9v-2a4 4 0 0 0-3-3.87M16 4.13A4 4 0 0 1 16 11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                <h3>Become a Volunteer</h3>
                <p>Use your time and skills to support our initiatives; especially Life Saver Army; and bring positive change to communities across India.</p>
              </article>
              <article className="way reveal d1">
                <div className="wic"><svg viewBox="0 0 24 24" fill="none"><path d="M5 21V5a2 2 0 0 1 2-2h7l5 5v13M5 21h14M9 8h3M9 12h6M9 16h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                <h3>Take a Pledge</h3>
                <p>Commit to being a lifesaver. Register as a blood or platelet donor with Life Saver Army and pledge to answer the call when a patient in your community needs you most.</p>
              </article>
              <article className="way reveal d2">
                <div className="wic"><svg viewBox="0 0 24 24" fill="none"><path d="M3 21h18M5 21V8l7-5 7 5v13M9 21v-5h6v5M9 11h.01M15 11h.01M12 11h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                <h3>Support NGOs</h3>
                <p>Partner with Life Saver Army to bring blood donation access to your local community. Connect us with hospitals, colleges, and institutions where we can deploy QR code stickers and organise donation camps.</p>
              </article>
              <article className="way reveal d3">
                <div className="wic"><Heart /></div>
                <h3>Donate</h3>
                <p>Your contribution directly funds Life Saver Army's operations; from QR code sticker deployment and donor registration camps to field coordinator support and emergency blood response infrastructure.</p>
              </article>
            </div>
          </div>
        </section>

        {/* ===================== TESTIMONIALS ===================== */}
        <section className="section section--mist testi">
          <div className="wrap" style={{ textAlign: "center" }}>
            <span className="eyebrow reveal" style={{ justifyContent: "center" }}>Testimonials</span>
            <h2 className="reveal d1" style={{ fontSize: "clamp(2rem,4.2vw,3.2rem)" }}>What people say about Jainam Jivika Foundation?</h2>
          </div>
          <div className="wrap">
            <div
              className="testi-track reveal d2"
              ref={tTrack}
              onMouseEnter={() => setTPaused(true)}
              onMouseLeave={() => setTPaused(false)}
            >
              {TESTIMONIALS.map((t, i) => (
                <figure className="testi-card" key={i}>
                  <svg className="testi-quote-mark" viewBox="0 0 48 36" aria-hidden="true">
                    <path d="M0 36V21.6C0 9.6 6.4 2.4 19.2 0l2.4 5.4C14.8 7.4 11.4 11.2 11 16.8h9.4V36H0zm27.6 0V21.6C27.6 9.6 34 2.4 46.8 0l2.4 5.4c-6.8 2-10.2 5.8-10.6 11.4H48V36H27.6z" fill="currentColor" />
                  </svg>
                  <blockquote>{t.quote}</blockquote>
                  <figcaption className="testi-person">
                    <div className="testi-avatar"><img src={t.img} alt={t.name} loading="lazy" /></div>
                    <div className="testi-meta"><div className="nm">{t.name}</div><div className="ro">{t.role}</div></div>
                  </figcaption>
                </figure>
              ))}
            </div>
            <div className="testi-nav">
              <button className="tn-btn" aria-label="Previous" onClick={() => tGo(tIdx - 1)}>
                <svg viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <div className="testi-dots">
                {Array.from({ length: tPages }, (_, i) => (
                  <button
                    type="button"
                    key={i}
                    className={"tdot" + (i === Math.min(tIdx, tPages - 1) ? " active" : "")}
                    onClick={() => tGo(i)}
                    aria-label={`Go to testimonial ${i + 1}`}
                  />
                ))}
              </div>
              <button className="tn-btn" aria-label="Next" onClick={() => tGo(tIdx + 1)}>
                <svg viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
          </div>
        </section>

        {/* ===================== VOLUNTEER ===================== */}
        <section className="section section--mist volunteer" id="join">
          <div className="wrap split">
            <div className="volunteer-media media-ring reveal">
              <div className="ph">
                <img src={HomeVolunteerHero} alt="Volunteers joining hands together" loading="lazy" />
              </div>
            </div>
            <div className="volunteer-copy reveal d1">
              <span className="eyebrow">Volunteer</span>
              <h2>Be the change you want to see!</h2>
              <p className="lead">
                Join Jainam Jivika Foundation as a volunteer and use your time, skills, and passion to uplift communities across India. Whether you are registering blood donors for Life Saver Army, organising awareness events, or supporting our field teams; your contribution transforms lives. Many have joined us. Why not you?
              </p>
              <p className="lead">
                Many have joined us. Why not you?
              </p>
              <a href="https://lifesaverarmy.com/volunteers" target="_blank" rel="noopener noreferrer" className="btn btn--amber" style={{ marginTop: "1.4rem" }}>
                Your Journey Towards Making an Impact Starts Here! <Heart />
              </a>
            </div>
          </div>
        </section>

        {/* ===================== VISIT US ===================== */}
        <section className="section visit" id="contact">
          <div className="wrap">
            <span className="eyebrow reveal">Reach us</span>
            <h2 className="reveal d1" style={{ fontSize: "clamp(2rem,4.2vw,3.2rem)", maxWidth: "18ch", lineHeight: 1.12 }}>Visit our office in Pune</h2>
            <p className="lead reveal d1" style={{ maxWidth: "48ch" }}>
              We’d love to welcome you. Drop by, write to us, or get directions to our office at Jainam Tower, Thergaon, Pune.
            </p>
            <div className="visit-grid">
              <div className="visit-info reveal d1">
                <div className="contact-list">
                  <div className="contact-row">
                    <span className="ic"><svg viewBox="0 0 24 24" fill="none"><path d="M12 21s-7-5.4-7-11a7 7 0 0 1 14 0c0 5.6-7 11-7 11Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" /></svg></span>
                    <div className="ct"><div className="k">Our Address</div><div className="v">Jainam Tower, Sai Park,<br />Thergaon,<br />Pune - 411033, Maharashtra, India.</div></div>
                  </div>
                  <div className="contact-row">
                    <span className="ic"><svg viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg></span>
                    <div className="ct"><div className="k">Email Us</div><div className="v"><a href="mailto:info@JJFIndia.com">info@JJFIndia.com</a></div></div>
                  </div>
                  <div className="contact-row">
                    <span className="ic"><svg viewBox="0 0 24 24" fill="none"><path d="M5 4h4l2 5-3 2a14 14 0 0 0 6 6l2-3 5 2v4a2 2 0 0 1-2 2A17 17 0 0 1 3 6a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg></span>
                    <div className="ct"><div className="k">Call Us</div><div className="v"><a href="tel:+919552895289">+91 95 5289 5289</a></div></div>
                  </div>
                </div>
              </div>
              <div className="map-card reveal d1">
                <a className="btn btn--amber dir-btn" target="_blank" rel="noopener noreferrer" href="https://www.google.com/maps/dir/?api=1&destination=Jainam+Tower+Sai+Park+Thergaon+Pune+411033">Get Directions →</a>
                <iframe
                  title="Jainam Jivika Foundation location map"
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  src="https://maps.google.com/maps?q=Jainam%20Tower%2C%20Sai%20Park%2C%20Thergaon%2C%20Pune%20411033&z=16&output=embed"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      </JjfShell>

      {selectedPopup && popupShown && (
        <PopupModal show={popupShown} onClose={handleClosePopup} popup={selectedPopup} />
      )}
    </Fragment>
  );
};

export default MainPage;
